import { useEffect, useRef, useState } from 'react';
import { useCloseRachelOnNavigate } from '../../hooks/useCloseRachelOnNavigate';
import { useIsRachelAvailable } from '../../hooks/useIsRachelAvailable';
import { useRachelPageNavigation } from '../../hooks/useRachelPageNavigation';
import { useRachelPresentation } from '../../hooks/useRachelPresentation';
import { useRachelStore } from '../../store/rachel.store';
import {
  selectCloseRachel,
  selectIsRachelInputFocused,
  selectIsRachelOpen,
  selectOpenRachel,
} from '../../store/rachel.store.selectors';
import { resolveRachelEscapeAction } from './rachelPanel.utils';

export const useRachelPanel = () => {
  const isAvailable = useIsRachelAvailable();
  const isOpen = useRachelStore(selectIsRachelOpen);
  const open = useRachelStore(selectOpenRachel);
  const close = useRachelStore(selectCloseRachel);
  const presentation = useRachelPresentation();
  const isInputFocused = useRachelStore(selectIsRachelInputFocused);
  const { leavePage, canGoBack } = useRachelPageNavigation();
  const [hasExited, setHasExited] = useState(true);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useCloseRachelOnNavigate();

  const handleEscape = () => {
    const action = resolveRachelEscapeAction({
      mode: presentation.mode,
      isOpen,
      canGoBack,
      isFocusInsidePanel:
        isInputFocused || Boolean(panelRef.current?.contains(document.activeElement)),
    });

    if (action === 'close') {
      close();
      return;
    }

    if (action === 'leavePage') {
      open();
      leavePage();
    }
  };

  const handleEscapeRef = useRef(handleEscape);

  useEffect(() => {
    handleEscapeRef.current = handleEscape;
  });

  useEffect(() => {
    if (!isAvailable) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleEscapeRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isAvailable]);

  const handleEnter = () => {
    setHasExited(false);

    if (!presentation.isOverlaid) {
      return;
    }

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
  };

  const handleExited = () => {
    setHasExited(true);
    previouslyFocusedRef.current?.focus();
    previouslyFocusedRef.current = null;
  };

  return {
    isAvailable,
    isOpen,
    presentation,
    hasExited,
    close,
    panelRef,
    handleEnter,
    handleExited,
  };
};
